import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import keyMirror from 'keymirror';

import t from '../../lib/I18n';
import CustomerService from '../../services/CustomerService';
import ModalActions from '../../actions/ModalActions';

import BaseModal from '../general/BaseModal';
import Icon from '../general/Icon';
import BAlert, {bAlertType} from '../general/BAlert';

const STATE = keyMirror({
    IDLE: null,
    LOADING: null,
    ERROR: null,
    SUCCESS: null,
});

function reducer(state, action) {
    switch (action.type) {
        case 'RESET_REQUESTED':
            if ([STATE.LOADING, STATE.SUCCESS].includes(state)) {
                return state;
            }
            return STATE.LOADING;
        case 'RESET_COMPLETED':
            return STATE.SUCCESS;
        case 'RESET_FAILED':
            return STATE.ERROR;
        default:
            return state;
    }
}

export default function AccountLockedModal({email, isDormant = false, isLocked = false}) {
    const [state, dispatch] = React.useReducer(reducer, STATE.IDLE);
    const requestPasswordReset = React.useCallback(async () => {
        try {
            dispatch({type: 'RESET_REQUESTED'});
            await CustomerService.requestUnlockAccount(email);
            dispatch({type: 'RESET_COMPLETED'});
        } catch (err) {
            dispatch({type: 'RESET_FAILED', error: err});
        }
    }, [email]);

    const modalProps = {
        header: false,
        disableEscape: true,
        hideContinue: isLocked,
        variant: 'light',
        className: 'modal--xs',
        continueText: 'login.accountLocked.button.reactivate',
        disableContinue: [STATE.LOADING, STATE.SUCCESS].includes(state),
        onContinue: requestPasswordReset,
        onClose: () => ModalActions.close(AccountLockedModal),
    };

    const title = useMemo(() => {
        if (isDormant) {
            return 'login.accountLocked.titleDormant';
        }

        if (isLocked) {
            return null;
        }

        return 'login.accountLocked.titleDefault';
    }, [isDormant, isLocked]);

    const message = useMemo(() => {
        if (isDormant) {
            return 'login.accountLocked.messageDormant';
        }

        if (isLocked) {
            return 'login.accountLocked.messageLocked';
        }

        return 'login.accountLocked.messageDefault';
    }, [isDormant, isLocked]);

    return (
        <BaseModal {...modalProps}>
            <section className="confirmation text-center">
                <Icon icon="error" className="margin-top margin-bottom otrl-xl" />
                {title ? <p className="text-lg">{t(title)}</p> : null}
            </section>

            <p>{t(message)}</p>

            {state === STATE.ERROR && (
                <BAlert type={bAlertType.DANGER}>{t('login.accountLocked.error')}</BAlert>
            )}
            {state === STATE.SUCCESS && (
                <BAlert type={bAlertType.SUCCESS}>{t('login.accountLocked.requestedReset')}</BAlert>
            )}
        </BaseModal>
    );
}

AccountLockedModal.propTypes = {
    email: PropTypes.string.isRequired,
    isDormant: PropTypes.bool,
    isLocked: PropTypes.bool,
};
